/* ─────────────────────────────────────────────────────────────
   SOLSHOCK • shared client-side behavior
   - Sticky header (transparent → solid on scroll)
   - Colorpop scroll reveal (IntersectionObserver)
   - Reveal-up sections
   - Mobile menu toggle
   - Cart drawer (Shopify AJAX Cart API)
   - Testimonial carousel
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // ── HEADER STICKY ────────────────────────────────────────────
  const header = document.querySelector('.ss-header');
  function onScroll() {
    if (!header) return;
    if (window.scrollY > 30) header.classList.add('scrolled');
    else header.classList.remove('scrolled');
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // ── COLORPOP REVEAL ──────────────────────────────────────────
  document.querySelectorAll('.colorpop').forEach((el) => {
    if (el.dataset.wrapped) return;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment();
          const parts = child.nodeValue.split(/(\s+)/);
          parts.forEach((p) => {
            if (p.trim().length === 0) {
              frag.appendChild(document.createTextNode(p));
            } else {
              const s = document.createElement('span');
              s.className = 'word';
              s.textContent = p;
              frag.appendChild(s);
            }
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.classList.contains('word')) return;
          if (child.hasAttribute('data-pop')) {
            child.classList.add('word');
            return;
          }
          walk(child);
        }
      });
    };
    walk(el);
    [...el.querySelectorAll('.word')].forEach((w, i) => {
      /* The intro paragraphs are long. At 0.06s per word the final line could
         wait several seconds after the paragraph entered view. Keep the wave,
         but let it travel at reading speed instead of making the reader wait. */
      w.style.setProperty('--d', (i * 0.025) + 's');
    });
    el.dataset.wrapped = '1';
  });

  const popObs = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('is-lit');
        popObs.unobserve(e.target);
      }
    });
  }, { threshold: 0, rootMargin: '0px 0px -25% 0px' });
  document.querySelectorAll('.colorpop').forEach((el) => popObs.observe(el));

  // ── REVEAL-UP ────────────────────────────────────────────────
  const upObs = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('in');
        upObs.unobserve(e.target);
      }
    });
  }, { threshold: 0.15 });
  document.querySelectorAll('.reveal-up').forEach((el) => upObs.observe(el));

  // ── MOBILE MENU ──────────────────────────────────────────────
  const hamburger = document.querySelector('.hamburger');
  const mobileMenu = document.querySelector('.mobile-menu');
  const mobileClose = document.querySelector('.mobile-menu-close');
  hamburger?.addEventListener('click', () => mobileMenu?.classList.add('open'));
  mobileClose?.addEventListener('click', () => mobileMenu?.classList.remove('open'));
  document.querySelectorAll('.mobile-menu a').forEach((a) =>
    a.addEventListener('click', () => mobileMenu?.classList.remove('open'))
  );

  // ── CART DRAWER (Shopify AJAX API) ───────────────────────────
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  function moneyFormat(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function openDrawer() {
    document.querySelector('.cart-drawer')?.classList.add('open');
    document.querySelector('.cart-drawer-overlay')?.classList.add('open');
  }
  function closeDrawer() {
    document.querySelector('.cart-drawer')?.classList.remove('open');
    document.querySelector('.cart-drawer-overlay')?.classList.remove('open');
  }

  function updateCartBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('empty', count === 0);
    });
  }

  function renderCartDrawer(cart) {
    const list  = document.querySelector('[data-cart-items]');
    const empty = document.querySelector('[data-cart-empty]');
    const sub   = document.querySelector('[data-cart-subtotal]');

    updateCartBadge(cart.item_count);
    if (sub) sub.textContent = moneyFormat(cart.total_price);

    if (!list) return;

    if (cart.item_count === 0) {
      list.innerHTML = '';
      if (empty) empty.style.display = 'block';
      return;
    }
    if (empty) empty.style.display = 'none';

    list.innerHTML = cart.items.map((item) => `
      <div class="cart-item" data-key="${escapeHtml(item.key)}">
        <div class="cart-item-img">
          ${item.image
            ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" style="width:70px;height:88px;object-fit:cover;border-radius:6px;">`
            : `<img src="${escapeHtml(window.SOLSHOCK_LOGO_SRC || '')}" alt="" style="width:50px;opacity:0.4;">`
          }
        </div>
        <div>
          <div class="cart-item-name">${escapeHtml(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item-variant">${escapeHtml(item.variant_title)}</div>`
            : ''}
          <div class="cart-item-qty">
            <button data-cart-dec="${escapeHtml(item.key)}" aria-label="Decrease">−</button>
            <span>${item.quantity}</span>
            <button data-cart-inc="${escapeHtml(item.key)}" aria-label="Increase">+</button>
          </div>
        </div>
        <div style="text-align:right">
          <div class="cart-item-price">${moneyFormat(item.line_price)}</div>
          <button class="cart-item-remove" data-cart-remove="${escapeHtml(item.key)}">Remove</button>
        </div>
      </div>
    `).join('');
  }

  function fetchAndRenderCart() {
    return fetch('/cart.js')
      .then(r => r.json())
      .then(cart => { renderCartDrawer(cart); return cart; });
  }

  // Intercept product form submissions for AJAX add-to-cart
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const data = new FormData(form);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    })
    .then(r => r.json())
    .then(() => fetchAndRenderCart())
    .then(() => openDrawer())
    .catch(() => {
      // Fallback: let Shopify handle it normally
      form.submit();
    });
  });

  // Cart drawer click events
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-cart]')) { e.preventDefault(); openDrawer(); }
    if (e.target.closest('[data-close-cart]')) { e.preventDefault(); closeDrawer(); }

    const incBtn = e.target.closest('[data-cart-inc]');
    const decBtn = e.target.closest('[data-cart-dec]');
    const rmBtn  = e.target.closest('[data-cart-remove]');

    if (incBtn || decBtn || rmBtn) {
      const key = (incBtn || decBtn || rmBtn).getAttribute(
        incBtn ? 'data-cart-inc' : decBtn ? 'data-cart-dec' : 'data-cart-remove'
      );
      // Find current quantity
      const itemEl = document.querySelector(`[data-key="${CSS.escape(key)}"]`);
      const currentQty = itemEl
        ? parseInt(itemEl.querySelector('.cart-item-qty span')?.textContent || '1')
        : 1;
      const newQty = rmBtn ? 0 : incBtn ? currentQty + 1 : Math.max(0, currentQty - 1);

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity: newQty })
      })
      .then(r => r.json())
      .then(cart => renderCartDrawer(cart));
    }
  });

  // Initial cart load
  fetchAndRenderCart();

  // ── YEAR INJECTION ───────────────────────────────────────────
  document.querySelectorAll('[data-year]').forEach(el => {
    el.textContent = new Date().getFullYear();
  });

  // ── TESTIMONIAL CAROUSEL ─────────────────────────────────────
  (function () {
    const root = document.querySelector('[data-carousel]');
    if (!root) return;
    const track = root.querySelector('[data-track]');
    const slides = [...track.children];
    const dotsWrap = root.querySelector('[data-dots]');
    const prev = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    let index = 0;
    let timer;

    slides.forEach((_, i) => {
      const d = document.createElement('button');
      d.setAttribute('aria-label', 'Go to testimonial ' + (i + 1));
      if (i === 0) d.classList.add('active');
      d.addEventListener('click', () => go(i, true));
      dotsWrap.appendChild(d);
    });

    function go(i, userInitiated) {
      index = (i + slides.length) % slides.length;
      const slide = slides[index];
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      [...dotsWrap.children].forEach((d, j) => d.classList.toggle('active', j === index));
      if (userInitiated) reset();
    }
    function autoplay() { go(index + 1); }
    function reset() { clearInterval(timer); timer = setInterval(autoplay, 6000); }

    prev.addEventListener('click', () => go(index - 1, true));
    next.addEventListener('click', () => go(index + 1, true));

    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const positions = slides.map(s => Math.abs(s.offsetLeft - track.scrollLeft - track.offsetLeft));
        const closest = positions.indexOf(Math.min(...positions));
        if (closest !== index) {
          index = closest;
          [...dotsWrap.children].forEach((d, j) => d.classList.toggle('active', j === index));
        }
      }, 100);
    });

    root.addEventListener('mouseenter', () => clearInterval(timer));
    root.addEventListener('mouseleave', reset);

    reset();
  })();

  // ── BUTTON PRESS STATE ───────────────────────────────────────
  // iOS Safari will not apply :active unless a touch listener is bound, and a
  // phone has no :hover at all — so the gradient fill never fired on a finger
  // even though the CSS for it was correct. Mirror the pressed state with an
  // explicit class. Delegated from the document, so it covers buttons that get
  // rendered later (cart drawer, PDP variant swaps) with no re-binding.
  const PRESSABLE = '.btn, .btn-gold, .sws-btn';

  const clearPressed = () => {
    document.querySelectorAll('.is-pressed').forEach((b) => b.classList.remove('is-pressed'));
  };

  document.addEventListener('pointerdown', (e) => {
    const btn = e.target.closest(PRESSABLE);
    if (btn) btn.classList.add('is-pressed');
  }, { passive: true });

  ['pointerup', 'pointercancel', 'dragstart'].forEach((evt) =>
    document.addEventListener(evt, clearPressed, { passive: true })
  );
  // A finger that slides off the button, and a page hidden mid-press, both
  // leave the state stuck lit otherwise.
  document.addEventListener('pointerleave', clearPressed, { passive: true });
  window.addEventListener('blur', clearPressed);

})();
